import { Injectable, Injector } from '@angular/core';
import { Router, NavigationEnd } from '@angular/router';
import { filter } from 'rxjs/operators';
import { environment } from '../../../environments/environment';
import { UserStore } from '../../store/user.store';
import { AcuStorageService } from '../../service/acustorage.service';
import { ConsoleLoggerService } from './console-logger.service';
import { ReplayRecorderService } from './replay-recorder.service';
import { SupportApiService } from '../../pages/support/support-api.service';
import { SupportModel } from '../../pages/support/supportModel';

export interface HttpLogEntry {
  timestamp: string;
  method: string;
  url: string;
  status: number;
  durationMs: number;
  error?: string;
}

@Injectable({
  providedIn: 'root'
})
export class DiagnosticsService {
  private recentHttpCalls: HttpLogEntry[] = [];
  private maxHttpLogs = 25;
  private routeHistory: string[] = [];
  private maxRouteHistory = 10;
  private recentCrashTimestamps: { [fingerprint: string]: number } = {};

  constructor(
    private consoleLogger: ConsoleLoggerService,
    private replayRecorder: ReplayRecorderService,
    private injector: Injector,
    private router: Router
  ) {
    this.initRouteTracking();
    // Start circular screen replay recorder
    this.replayRecorder.startRecording();
  }

  private initRouteTracking(): void {
    this.router.events
      .pipe(filter(event => event instanceof NavigationEnd))
      .subscribe((event: any) => {
        this.routeHistory.push(`${new Date().toISOString().substring(11, 19)} -> ${event.urlAfterRedirects || event.url}`);
        if (this.routeHistory.length > this.maxRouteHistory) {
          this.routeHistory.shift();
        }
      });
  }

  public logHttpCall(entry: HttpLogEntry): void {
    this.recentHttpCalls.push(entry);
    if (this.recentHttpCalls.length > this.maxHttpLogs) {
      this.recentHttpCalls.shift();
    }
  }

  public getEnvironmentSnapshot(): any {
    let userLoggedInfo = 'Non loggato';
    try {
      const userStore = this.injector.get(UserStore);
      const acuStorage = this.injector.get(AcuStorageService);
      if (userStore?.userLogged) {
        userLoggedInfo = `${userStore.userLogged.firstName} ${userStore.userLogged.lastName} (${userStore.userLogged.email}) [ID: ${userStore.userLogged.id}] - Ruoli: [${userStore.userLogged.roles?.join(', ')}]`;
      } else {
        const lastUser = acuStorage?.getLastKnownUser<any>();
        if (lastUser) {
          userLoggedInfo = `${lastUser.firstName || ''} ${lastUser.lastName || ''} (${lastUser.email || ''}) [ID: ${lastUser.id || ''}] - Ruoli: [${(lastUser.roles || []).join(', ')}] (Disconnesso / Sessione scaduta)`;
        }
      }
    } catch (e) {
      // UserStore / AcuStorage might not be ready
    }

    return {
      version: environment.acuplaceVersion,
      userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : 'Unknown',
      screen: typeof window !== 'undefined' ? `${window.innerWidth}x${window.innerHeight}` : 'N/A',
      connection: typeof navigator !== 'undefined' && navigator.onLine ? 'Online' : 'Offline',
      url: typeof window !== 'undefined' ? window.location.href : '',
      currentRoute: this.router.url,
      routeHistory: [...this.routeHistory],
      user: userLoggedInfo,
      timestamp: new Date().toISOString()
    };
  }

  /**
   * Builds the formatted markdown description and extracts the compressed replay package
   */
  public generateReportData(userDescription: string, category: string = 'GENERICO'): {
    description: string;
    replayGzipBase64: string;
    metadata: any;
  } {
    const env = this.getEnvironmentSnapshot();
    const logs = this.consoleLogger.getFormattedLogs(40);
    const replay = this.replayRecorder.getCompressedReplay();
    const replayDuration = this.replayRecorder.getBufferDurationSeconds();

    // Format recent HTTP calls
    const httpFormatted = this.recentHttpCalls
      .slice(-10)
      .map(h => `[${h.timestamp.substring(11, 19)}] ${h.method} ${h.url} -> ${h.status} (${h.durationMs}ms)${h.error ? ` [ERR: ${h.error}]` : ''}`)
      .join('\n');

    const description = `${userDescription}\n\n` +
      `========================================\n` +
      `🔍 [DIAGNOSTICA FRONTEND AUTOMATICA]\n` +
      `========================================\n` +
      `• Categoria / Caso Test: ${category}\n` +
      `• Data & Ora: ${env.timestamp}\n` +
      `• Pagina / Rotta: ${env.currentRoute}\n` +
      `• Utente: ${env.user}\n` +
      `• Versione: ${env.version}\n` +
      `• Schermo & Rete: ${env.screen} | ${env.connection}\n` +
      `• User Agent: ${env.userAgent}\n` +
      `• Video Replay: ${replayDuration}s registrati (Eventi: ${this.replayRecorder.getEventCount()}, Raw: ${(replay.rawSize / 1024).toFixed(1)} KB, Compresso: ${(replay.compressedSize / 1024).toFixed(1)} KB)\n\n` +
      `📍 [STORICO NAVIGAZIONE (Ultime rotte)]\n` +
      `${env.routeHistory.length > 0 ? env.routeHistory.join('\n') : 'N/A'}\n\n` +
      `🌐 [ULTIME CHIAMATE HTTP (Client-side)]\n` +
      `\`\`\`\n${httpFormatted || 'Nessuna chiamata HTTP recente'}\n\`\`\`\n\n` +
      `💻 [CONSOLE LOGS RECENTI (Errori e Warning)]\n` +
      `\`\`\`\n${logs || 'Nessun log in console'}\n\`\`\`\n`;

    return {
      description,
      replayGzipBase64: replay.base64,
      metadata: env
    };
  }

  public isBotUserAgent(userAgent?: string): boolean {
    if (!userAgent) return false;
    const ua = userAgent.toLowerCase();
    const botPatterns = [
      'bot',
      'crawler',
      'spider',
      'meta-webindexer',
      'facebookexternalhit',
      'bytespider',
      'yandex',
      'googlebot',
      'bingbot',
      'slurp',
      'duckduckbot',
      'baiduspider',
      'twitterbot',
      'linkedinbot',
      'embedly',
      'quora link preview',
      'showyoubot',
      'outbrain',
      'pinterest',
      'slackbot',
      'vkshare',
      'w3c_validator',
      'headlesschrome'
    ];
    return botPatterns.some(pattern => ua.includes(pattern));
  }

  /**
   * Called automatically by GlobalErrorHandler on uncaught exceptions
   */
  public async handleAutoCrash(error: any): Promise<void> {
    const errorMessage = error?.message || String(error);
    const stack = error?.stack || '';

    // Ignore benign / non-fatal framework overlay lifecycle errors (e.g. double dismiss, already closed modal/popover)
    if (errorMessage.includes('overlay does not exist')) {
      console.warn('[Diagnostics] Suppressing benign overlay lifecycle error from crash reports:', errorMessage);
      return;
    }

    // Suppress benign gapi loading / adblocker / network errors
    if (errorMessage.includes('gapi is not defined')) {
      console.warn('[Diagnostics] Suppressing gapi is not defined crash report (adblocker/bot/network):', errorMessage);
      return;
    }

    // Suppress crash reports from automated bots, web crawlers, and indexers (incomplete JS environment)
    const userAgent = typeof navigator !== 'undefined' ? navigator.userAgent : '';
    if (this.isBotUserAgent(userAgent)) {
      console.warn('[Diagnostics] Suppressing crash report from bot/crawler:', userAgent);
      return;
    }

    // Suppress HTTP 404 Not Found errors from auto crash reports (standard REST missing resource)
    if (error?.status === 404 || errorMessage.includes('404 OK') || errorMessage.includes('404 Not Found')) {
      console.warn('[Diagnostics] Suppressing HTTP 404 error from crash reports:', errorMessage);
      return;
    }

    // Suppress Angular router NG04002 errors caused by missing static assets or crawler URL probes
    if (errorMessage.includes('NG04002') && (errorMessage.includes('assets/') || /\.(png|ico|svg|jpg|jpeg|webp|json|xml|txt)/i.test(errorMessage))) {
      console.warn('[Diagnostics] Suppressing static asset NG04002 route mismatch from crash reports:', errorMessage);
      return;
    }

    // Suppress HTTP 401 Unauthorized and 403 Forbidden errors from auto crash reports (auth/permission denials)
    if (
      error?.status === 401 ||
      error?.status === 403 ||
      errorMessage.includes('401 OK') ||
      errorMessage.includes('401 Unauthorized') ||
      errorMessage.includes('403 OK') ||
      errorMessage.includes('403 Forbidden')
    ) {
      console.warn('[Diagnostics] Suppressing HTTP 401/403 auth error from crash reports:', errorMessage);
      return;
    }

    const fingerprint = `${errorMessage.slice(0, 80)}_${stack.slice(0, 100)}`;

    const now = Date.now();
    const lastReportTime = this.recentCrashTimestamps[fingerprint] || 0;

    // Rate-limit: avoid spamming Jira for the same unhandled error (max once every 10 minutes)
    if (now - lastReportTime < 10 * 60 * 1000) {
      console.warn('[Diagnostics] Suppressing duplicate crash report for error:', errorMessage);
      return;
    }
    this.recentCrashTimestamps[fingerprint] = now;

    console.warn('[Diagnostics] Unhandled crash detected, packaging diagnostic report...');

    try {
      // Use logged user's email if available so the Customer field reflects who experienced the crash.
      // The Jira ticket reporter remains the technical account (Acus).
      let reporterEmail = 'unhandled-crash@acuplace.local';
      try {
        const userStore = this.injector.get(UserStore);
        const acuStorage = this.injector.get(AcuStorageService);
        const userEmail = userStore?.userLogged?.email || acuStorage?.getLastKnownUser<any>()?.email;
        if (userEmail) {
          reporterEmail = userEmail;
        }
      } catch (e) {
        // UserStore / AcuStorage might not be initialized
      }

      const crashDescription = `🚨 [CRASH NON GESTITO RILEVATO]\n\n` +
        `**Errore:** ${errorMessage}\n\n` +
        `**Stack Trace:**\n\`\`\`\n${stack}\n\`\`\``;

      const report = this.generateReportData(crashDescription, 'UNCAUGHT_EXCEPTION');

      const supportModel: SupportModel = {
        data: {
          reporter: reporterEmail,
          summary: `[TECH_BUG] [AUTO_CRASH] ${errorMessage.slice(0, 50)}`,
          description: report.description,
          acuplaceVersion: environment.acuplaceVersion,
          device: typeof navigator !== 'undefined' ? navigator.userAgent : '',
          requestType: 'TECH_BUG',
          replayData: report.replayGzipBase64,
          diagnostics: report.metadata
        }
      };

      const supportApi = this.injector.get(SupportApiService);
      const response = await supportApi.sendMessageToSupport(supportModel);
      console.info('[Diagnostics] Auto crash ticket created successfully:', response?.issueCreated || response);
    } catch (err) {
      console.error('[Diagnostics] Failed to send auto crash ticket:', err);
    }
  }
}
